import { Button, Stack, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/ko';

export interface PeriodRange {
  from: Dayjs;
  to: Dayjs;
}

interface Props {
  value: PeriodRange;
  onChange: (range: PeriodRange) => void;
}

const PRESETS: { label: string; days: number }[] = [
  { label: '1주', days: 7 },
  { label: '2주', days: 14 },
  { label: '4주', days: 28 },
  { label: '3개월', days: 90 },
];

const MAX_RANGE_DAYS = 366;

/**
 * 의뢰서 5-2 탭2 기간 선택 (P5-2).
 *
 * - 단축 버튼: 1주 / 2주 / 4주 / 3개월 (오늘 기준 N일 전 ~ 오늘)
 * - 직접 입력: DatePicker 2개(from/to)
 *   * 의뢰서는 "DatePicker range"였지만 MUI X range는 Pro(유료) — 단일 2개로 대체
 *   * from은 to를 maxDate로, to는 from을 minDate로 제약해 잘못된 범위 사전 차단
 * - 백엔드 P5-1 endpoint와 동일하게 최대 366일 제한
 */
export function PeriodPickerBar({ value, onChange }: Props) {
  const applyPreset = (days: number) => {
    const to = dayjs().startOf('day');
    const from = to.subtract(days - 1, 'day');
    onChange({ from, to });
  };

  const activePresetDays = (() => {
    const todayStart = dayjs().startOf('day');
    if (!value.to.isSame(todayStart, 'day')) return null;
    const diff = value.to.diff(value.from, 'day') + 1;
    return PRESETS.find((p) => p.days === diff)?.days ?? null;
  })();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {PRESETS.map((p) => (
            <Button
              key={p.label}
              size="small"
              variant={activePresetDays === p.days ? 'contained' : 'outlined'}
              onClick={() => applyPreset(p.days)}
            >
              {p.label}
            </Button>
          ))}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <DatePicker
            label="시작일"
            value={value.from}
            maxDate={value.to}
            minDate={value.to.subtract(MAX_RANGE_DAYS - 1, 'day')}
            onChange={(v) => {
              if (v && v.isValid()) onChange({ from: v.startOf('day'), to: value.to });
            }}
            format="YYYY-MM-DD"
            slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
          />
          <Typography variant="body2" color="text.secondary">
            ~
          </Typography>
          <DatePicker
            label="종료일"
            value={value.to}
            minDate={value.from}
            maxDate={value.from.add(MAX_RANGE_DAYS - 1, 'day')}
            onChange={(v) => {
              if (v && v.isValid()) onChange({ from: value.from, to: v.startOf('day') });
            }}
            format="YYYY-MM-DD"
            slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
          />
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
}
